/**
 * @fileoverview Color utility functions for color manipulation.
 * Handles color transformations and adjustments.
 */

/**
 * Color utility class for color manipulation operations.
 */
export class ColorUtils {
    /**
     * Create a lighter version of a hex color by mixing with white.
     * @param hex - Hex color code (with or without #)
     * @param percent - Percentage to lighten (0.0 to 1.0)
     * @returns Lightened hex color
     */
    static lightenColor(hex: string, percent: number): string {
        // Remove # if present
        hex = hex.replace('#', '');
        
        // Convert to RGB
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Lighten by mixing with white
        const newR = Math.round(r + (255 - r) * percent);
        const newG = Math.round(g + (255 - g) * percent);
        const newB = Math.round(b + (255 - b) * percent);
        
        // Convert back to hex
        return '#' + [newR, newG, newB].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('');
    }

    /**
     * Create a darker version of a hex color by mixing with black.
     * @param hex - Hex color code (with or without #)
     * @param percent - Percentage to darken (0.0 to 1.0)
     * @returns Darkened hex color
     */
    static darkenColor(hex: string, percent: number): string {
        // Remove # if present
        hex = hex.replace('#', '');
        
        // Convert to RGB
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Darken by reducing toward black
        const newR = Math.round(r * (1 - percent));
        const newG = Math.round(g * (1 - percent));
        const newB = Math.round(b * (1 - percent));
        
        // Convert back to hex
        return '#' + [newR, newG, newB].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('');
    }
}
